/**
 * AI Developer Training Day 1
 *
 * ENGLISH VERSION using standardized design system
 * All slides use consistent layouts, spacing, and typography
 */

import type { PresentationData } from '../../types/presentation'
import {
  StandardTitleSlide,
  StandardContentSlide,
  StandardGridSlide,
  FeatureCard,
  InfoBox,
  AgendaItem,
} from '../../components/presentation'
import { IconSolid } from '../../components/presentation'

export const developerTrainingDay1: PresentationData = {
  metadata: {
    title: 'AI Developer Training Day 1',
    description: 'AI Fundamentals & AI Coding Tools for Developers',
    author: 'AI Experts & Development Consultants',
    date: 'October 2025',
    tags: ['AI', 'AI Tools', 'Development', 'Workshop'],
  },
  slides: [
    // ============================================
    // SLIDE 1: Title
    // ============================================
    {
      id: 1,
      component: () => (
        <StandardTitleSlide
          title="AI Developer Training Day 1"
          subtitle="AI Fundamentals & AI Coding Tools for Developers"
          author="AI Experts & Development Consultants"
          date="October 2025"
          tags={['8 Hours Training', 'All Levels', 'Hands-on']}
          background="primary"
          footer={
            <div className="flex flex-wrap justify-center gap-6 text-white pb-4">
              <div className="text-center">
                <IconSolid name="clock" size="lg" className="mx-auto mb-1" />
                <p className="text-sm font-semibold">Full Day</p>
              </div>
              <div className="text-center">
                <IconSolid name="code" size="lg" className="mx-auto mb-1" />
                <p className="text-sm font-semibold">Hands-on</p>
              </div>
              <div className="text-center">
                <IconSolid name="graduation-cap" size="lg" className="mx-auto mb-1" />
                <p className="text-sm font-semibold">Certificate</p>
              </div>
            </div>
          }
        />
      ),
      transition: 'fade',
    },

    // ============================================
    // SLIDE 2: Agenda
    // ============================================
    {
      id: 2,
      component: () => (
        <StandardContentSlide
          title="Training Agenda"
          subtitle="What we'll cover today"
          background="light"
        >
          <div className="max-w-3xl mx-auto space-y-4">
            <AgendaItem
              number={1}
              title="AI Fundamentals"
              description="Understanding AI, Machine Learning, and Large Language Models"
              duration="45 min"
            />
            <AgendaItem
              number={2}
              title="AI Coding Tools Overview"
              description="GitHub Copilot, Cursor, ChatGPT, and more"
              duration="60 min"
            />
            <AgendaItem
              number={3}
              title="Hands-on Workshop"
              description="Practical exercises with AI coding assistants"
              duration="120 min"
            />
            <AgendaItem
              number={4}
              title="Best Practices & Patterns"
              description="How to effectively use AI in your development workflow"
              duration="60 min"
            />
            <AgendaItem
              number={5}
              title="Q&A and Next Steps"
              description="Open discussion and resources for continued learning"
              duration="30 min"
            />
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 3: AI Fundamentals
    // ============================================
    {
      id: 3,
      component: () => (
        <StandardGridSlide
          title="Understanding AI"
          subtitle="Key concepts every developer should know"
          columns={2}
          background="secondary"
          items={[
            <FeatureCard
              icon={<IconSolid name="brain" className="w-12 h-12 text-blue-600" />}
              title="What is AI?"
              description="Artificial Intelligence is the simulation of human intelligence by machines"
              items={['Machine Learning', 'Deep Learning', 'Neural Networks']}
            />,
            <FeatureCard
              icon={<IconSolid name="robot" className="w-12 h-12 text-green-600" />}
              title="Large Language Models"
              description="AI systems trained on vast amounts of text data"
              items={['GPT-4', 'Claude', 'Gemini', 'LLaMA']}
            />,
            <FeatureCard
              icon={<IconSolid name="code" className="w-12 h-12 text-purple-600" />}
              title="AI for Developers"
              description="How AI is transforming software development"
              items={['Code Generation', 'Bug Detection', 'Refactoring', 'Documentation']}
            />,
            <FeatureCard
              icon={<IconSolid name="chart-line" className="w-12 h-12 text-orange-600" />}
              title="Current State"
              description="AI capabilities and limitations in 2025"
              items={['Strengths', 'Weaknesses', 'Best Use Cases']}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 4: How LLMs Work
    // ============================================
    {
      id: 4,
      component: () => (
        <StandardContentSlide
          title="How LLMs Work"
          subtitle="A simplified explanation"
          background="light"
        >
          <div className="max-w-4xl mx-auto space-y-8">
            <InfoBox variant="tip">
              <strong>Key Insight:</strong> LLMs predict the next token based on patterns learned from training data
            </InfoBox>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-xl">
                <div className="text-3xl mb-3">📚</div>
                <h4 className="font-bold mb-2">Training</h4>
                <p className="text-sm">Models learn from billions of text examples</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-xl">
                <div className="text-3xl mb-3">🎯</div>
                <h4 className="font-bold mb-2">Fine-tuning</h4>
                <p className="text-sm">Specialized training for specific tasks</p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-xl">
                <div className="text-3xl mb-3">💬</div>
                <h4 className="font-bold mb-2">Inference</h4>
                <p className="text-sm">Generating responses based on input</p>
              </div>
            </div>

            <InfoBox variant="warning">
              <strong>Important:</strong> LLMs don't "think" like humans - they pattern match based on training data
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 5: AI Coding Tools Overview
    // ============================================
    {
      id: 5,
      component: () => (
        <StandardGridSlide
          title="AI Coding Tools Landscape"
          subtitle="Popular tools and their strengths"
          columns={3}
          background="primary"
          items={[
            <FeatureCard
              icon={<IconSolid name="github" className="w-12 h-12 text-white" />}
              title="GitHub Copilot"
              description="AI pair programmer by GitHub & OpenAI"
              items={['IDE Integration', 'Code Completion', 'Multi-language support']}
            />,
            <FeatureCard
              icon={<IconSolid name="comment-dots" className="w-12 h-12 text-white" />}
              title="ChatGPT/Claude"
              description="Conversational AI for coding assistance"
              items={['Problem Solving', 'Code Explanation', 'Learning Resource']}
            />,
            <FeatureCard
              icon={<IconSolid name="keyboard" className="w-12 h-12 text-white" />}
              title="Cursor Editor"
              description="AI-first code editor"
              items={['Built-in AI', 'Codebase Awareness', 'Refactoring']}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 6: Prompt Engineering Basics
    // ============================================
    {
      id: 6,
      component: () => (
        <StandardContentSlide
          title="Prompt Engineering for Developers"
          subtitle="Getting better results from AI tools"
          background="light"
        >
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded">
              <h4 className="font-bold text-red-700 dark:text-red-300 mb-2">❌ Poor Prompt</h4>
              <code className="text-sm">"Fix this code"</code>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded">
              <h4 className="font-bold text-green-700 dark:text-green-300 mb-2">✅ Good Prompt</h4>
              <code className="text-sm">
                "Review this React component for bugs. Focus on: state management issues, memory leaks, and accessibility problems. Suggest fixes with explanations."
              </code>
            </div>

            <InfoBox variant="tip">
              <strong>Prompt Formula:</strong> Context + Task + Constraints + Expected Output = Better Results
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 7: Hands-on Exercise
    // ============================================
    {
      id: 7,
      component: () => (
        <StandardContentSlide
          title="Hands-on Exercise"
          subtitle="Let's practice together"
          background="secondary"
        >
          <div className="max-w-3xl mx-auto space-y-8">
            <div className="text-center">
              <IconSolid name="laptop-code" className="w-20 h-20 text-blue-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-4">Exercise: Build a Todo App</h3>
            </div>

            <div className="space-y-4">
              <FeatureCard
                icon={<IconSolid name="clock" className="w-8 h-8 text-blue-600" />}
                title="Step 1: Setup (10 min)"
                description="Use AI to generate project scaffold with your preferred stack"
              />
              <FeatureCard
                icon={<IconSolid name="tasks" className="w-8 h-8 text-green-600" />}
                title="Step 2: Core Features (20 min)"
                description="Build add, toggle, and delete functionality with AI assistance"
              />
              <FeatureCard
                icon={<IconSolid name="magic" className="w-8 h-8 text-purple-600" />}
                title="Step 3: Refinement (15 min)"
                description="Use AI to review, refactor, and improve the code"
              />
            </div>

            <InfoBox variant="success">
              <strong>Goal:</strong> Compare your experience coding with vs. without AI assistance
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 8: Best Practices
    // ============================================
    {
      id: 8,
      component: () => (
        <StandardGridSlide
          title="Best Practices for AI-Assisted Development"
          subtitle="How to be effective and safe"
          columns={2}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="check-circle" className="w-12 h-12 text-green-600" />}
              title="DO: Verify Everything"
              description="Always review and test AI-generated code"
              items={['Security Review', 'Testing', 'Code Review']}
            />,
            <FeatureCard
              icon={<IconSolid name="times-circle" className="w-12 h-12 text-red-600" />}
              title="DON'T: Blind Trust"
              description="Never accept AI output without understanding"
              items={['Know the code', 'Ask questions', 'Learn from it']}
            />,
            <FeatureCard
              icon={<IconSolid name="shield-alt" className="w-12 h-12 text-blue-600" />}
              title="Security First"
              description="Be careful with sensitive data"
              items={['No secrets in prompts', 'No proprietary code', 'Sanitize output']}
            />,
            <FeatureCard
              icon={<IconSolid name="graduation-cap" className="w-12 h-12 text-purple-600" />}
              title="Keep Learning"
              description="Use AI as a learning tool"
              items={['Understand solutions', 'Explore alternatives', 'Build intuition']}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 9: Common Pitfalls
    // ============================================
    {
      id: 9,
      component: () => (
        <StandardContentSlide
          title="Common Pitfalls to Avoid"
          subtitle="Learn from others' mistakes"
          background="secondary"
        >
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-xl">
                <h4 className="font-bold text-red-700 dark:text-red-300 mb-3 flex items-center gap-2">
                  <IconSolid name="exclamation-triangle" className="w-6 h-6" />
                  Over-reliance
                </h4>
                <p className="text-sm">Depending too much on AI can stunt your growth as a developer</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-xl">
                <h4 className="font-bold text-red-700 dark:text-red-300 mb-3 flex items-center gap-2">
                  <IconSolid name="bug" className="w-6 h-6" />
                  Hidden Bugs
                </h4>
                <p className="text-sm">AI can introduce subtle bugs that only appear in edge cases</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-xl">
                <h4 className="font-bold text-red-700 dark:text-red-300 mb-3 flex items-center gap-2">
                  <IconSolid name="file-code" className="w-6 h-6" />
                  Context Issues
                </h4>
                <p className="text-sm">AI may not understand your full codebase context</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-xl">
                <h4 className="font-bold text-red-700 dark:text-red-300 mb-3 flex items-center gap-2">
                  <IconSolid name="sync" className="w-6 h-6" />
                  Outdated Info
                </h4>
                <p className="text-sm">AI knowledge cutoffs mean missing latest APIs/features</p>
              </div>
            </div>

            <InfoBox variant="tip">
              <strong>Remember:</strong> AI is a powerful assistant, but you are still the pilot. Always stay in control.
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 10: Building Effective Prompts
    // ============================================
    {
      id: 10,
      component: () => (
        <StandardContentSlide
          title="Building Effective Prompts"
          subtitle="A framework for better AI interactions"
          background="light"
        >
          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-900 text-cyan-400 p-6 rounded-lg font-mono text-sm mb-6">
              <div className="space-y-4">
                <div>
                  <span className="text-green-400">// 1. SET CONTEXT</span><br/>
                  <span className="text-white">"You are a senior React developer helping me build a todo app."</span>
                </div>
                <div>
                  <span className="text-green-400">// 2. DESCRIBE TASK</span><br/>
                  <span className="text-white">"Create a component that displays a list of todos."</span>
                </div>
                <div>
                  <span className="text-green-400">// 3. SPECIFY REQUIREMENTS</span><br/>
                  <span className="text-white">"- Use TypeScript<br/>
                  - Include checkboxes for completion<br/>
                  - Add delete button for each item<br/>
                  - Use Tailwind CSS for styling"</span>
                </div>
                <div>
                  <span className="text-green-400">// 4. DEFINE CONSTRAINTS</span><br/>
                  <span className="text-white">"- Keep it under 100 lines<br/>
                  - Make it accessible<br/>
                  - Add error handling"</span>
                </div>
              </div>
            </div>

            <InfoBox variant="success">
              <strong>Pro Tip:</strong> Save your effective prompts as templates for future use
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 11: Workshop Activity
    // ============================================
    {
      id: 11,
      component: () => (
        <StandardContentSlide
          title="Workshop Activity"
          subtitle="Practice your prompt engineering skills"
          background="secondary"
        >
          <div className="max-w-3xl mx-auto space-y-8">
            <div className="text-center">
              <IconSolid name="users" className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold">Pair Prompting Challenge</h3>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-xl">
              <h4 className="font-bold mb-4">The Challenge:</h4>
              <p className="mb-4">Work in pairs. One person describes a coding task, the other writes the perfect prompt to get it done by AI.</p>
              <ul className="space-y-2 text-sm">
                <li>1. Pick a task from the list (or create your own)</li>
                <li>2. Write the best prompt you can</li>
                <li>3. Run it and see what you get</li>
                <li>4. Iterate and improve</li>
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded">
                <h5 className="font-bold mb-2">Task Ideas:</h5>
                <ul className="text-sm space-y-1">
                  <li>• Form validation</li>
                  <li>• Data fetching hook</li>
                  <li>• Debounce function</li>
                  <li>• API client wrapper</li>
                </ul>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded">
                <h5 className="font-bold mb-2">Evaluation Criteria:</h5>
                <ul className="text-sm space-y-1">
                  <li>• Code quality</li>
                  <li>• Correctness</li>
                  <li>• Best practices</li>
                  <li>• Iterations needed</li>
                </ul>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 12: Resources for Continued Learning
    // ============================================
    {
      id: 12,
      component: () => (
        <StandardGridSlide
          title="Resources for Continued Learning"
          subtitle="Keep improving your AI-assisted development skills"
          columns={2}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="book" className="w-12 h-12 text-blue-600" />}
              title="Documentation"
              description="Official docs and guides"
              items={['GitHub Copilot Docs', 'OpenAI Docs', 'Anthropic Docs']}
            />,
            <FeatureCard
              icon={<IconSolid name="video" className="w-12 h-12 text-red-600" />}
              title="Video Courses"
              description="Learn from experts"
              items={['Prompt Engineering', 'AI for Devs', 'Best Practices']}
            />,
            <FeatureCard
              icon={<IconSolid name="newspaper" className="w-12 h-12 text-green-600" />}
              title="Blogs & Articles"
              description="Stay up to date"
              items={['AI News', 'Case Studies', 'Research Papers']}
            />,
            <FeatureCard
              icon={<IconSolid name="users" className="w-12 h-12 text-purple-600" />}
              title="Community"
              description="Learn from others"
              items={['Forums', 'Discord Servers', 'Reddit Communities']}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 13: Key Takeaways
    // ============================================
    {
      id: 13,
      component: () => (
        <StandardContentSlide
          title="Key Takeaways"
          subtitle="What we learned today"
          background="primary"
        >
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur p-6 rounded-xl">
                <IconSolid name="lightbulb" className="w-12 h-12 text-yellow-400 mb-4" />
                <h4 className="font-bold text-white text-lg mb-2">AI is a Tool</h4>
                <p className="text-white/80">AI enhances your capabilities but doesn't replace your expertise</p>
              </div>
              <div className="bg-white/10 backdrop-blur p-6 rounded-xl">
                <IconSolid name="comments" className="w-12 h-12 text-blue-400 mb-4" />
                <h4 className="font-bold text-white text-lg mb-2">Communication Matters</h4>
                <p className="text-white/80">Better prompts = better results. Learn to communicate effectively with AI</p>
              </div>
              <div className="bg-white/10 backdrop-blur p-6 rounded-xl">
                <IconSolid name="shield-alt" className="w-12 h-12 text-green-400 mb-4" />
                <h4 className="font-bold text-white text-lg mb-2">Verify Everything</h4>
                <p className="text-white/80">Always review and understand AI-generated code before using it</p>
              </div>
              <div className="bg-white/10 backdrop-blur p-6 rounded-xl">
                <IconSolid name="graduation-cap" className="w-12 h-12 text-purple-400 mb-4" />
                <h4 className="font-bold text-white text-lg mb-2">Keep Learning</h4>
                <p className="text-white/80">AI evolves rapidly. Stay curious and keep improving your skills</p>
              </div>
            </div>

            <InfoBox variant="success">
              <strong>Next Step:</strong> Practice what you learned today in your real projects. Start small, iterate often!
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 14: Q&A
    // ============================================
    {
      id: 14,
      component: () => (
        <StandardContentSlide
          title="Questions & Answers"
          subtitle="Let's discuss"
          background="light"
        >
          <div className="max-w-3xl mx-auto text-center space-y-12">
            <div>
              <IconSolid name="question-circle" className="w-24 h-24 text-blue-600 mx-auto mb-6" />
              <h3 className="text-3xl font-bold mb-4">What questions do you have?</h3>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                Topics we can explore:
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
                <IconSolid name="code" className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Specific Tools</p>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
                <IconSolid name="industry" className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Your Use Cases</p>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
                <IconSolid name="road" className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Career Impact</p>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
                <IconSolid name="rocket" className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Future of Dev</p>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 15: Thank You
    // ============================================
    {
      id: 15,
      component: () => (
        <StandardContentSlide
          title="Thank You!"
          subtitle="Keep building with AI"
          background="primary"
        >
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <div>
              <IconSolid name="heart" className="w-20 h-20 text-red-400 mx-auto mb-6" />
              <h3 className="text-3xl font-bold text-white mb-4">Thanks for attending!</h3>
              <p className="text-xl text-white/80">
                We hope this training helps you code faster and better with AI.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur p-6 rounded-xl">
                <IconSolid name="envelope" className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                <h4 className="font-bold text-white mb-2">Get in Touch</h4>
                <p className="text-white/80 text-sm">questions@training.com</p>
              </div>
              <div className="bg-white/10 backdrop-blur p-6 rounded-xl">
                <IconSolid name="github" className="w-10 h-10 text-white mx-auto mb-3" />
                <h4 className="font-bold text-white mb-2">GitHub</h4>
                <p className="text-white/80 text-sm">github.com/ai-training</p>
              </div>
              <div className="bg-white/10 backdrop-blur p-6 rounded-xl">
                <IconSolid name="calendar" className="w-10 h-10 text-green-400 mx-auto mb-3" />
                <h4 className="font-bold text-white mb-2">Day 2 Training</h4>
                <p className="text-white/80 text-sm">Coming Soon!</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur p-6 rounded-xl">
              <p className="text-white/90 italic">
                "The best way to predict the future is to create it with AI."
              </p>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },
  ],
}
